import { useState, useEffect, useCallback } from "react";

import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useSearchParams } from "react-router-dom";

import "../AuthShared.scss";
import niraLogoLong from "../../assets/NIRALogo_long.webp";
import { verifyInviteToken, acceptInvite } from "../../services/auth";
import { logger } from "../../utils/logger";

function SetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const verifyToken = useCallback(async () => {
    if (!token) {
      setTokenError("No invitation token provided. Please use the link from your email.");
      setVerifying(false);
      return;
    }

    try {
      const result = await verifyInviteToken(token);
      if (result.valid) {
        setTokenValid(true);
        setInviteName(result.name || "");
        setInviteEmail(result.email || "");
      } else {
        setTokenError(result.error || "Invalid or expired invitation link.");
      }
    } catch (error) {
      logger.error("Invite token verification error", { error, context: "SetPassword" });
      setTokenError("Unable to verify invitation. Please try again.");
    } finally {
      setVerifying(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    if (password !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const result = await acceptInvite(token, password);
      setSuccessMsg(result.message);
    } catch (error) {
      logger.error("Accept invite error", { error, context: "SetPassword" });
      setErrorMsg(error instanceof Error ? error.message : "Failed to set password.");
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (verifying) {
      return (
        <>
          <h2 className="auth-title">Verifying Invitation...</h2>
          <p className="auth-subtitle">Please wait while we verify your invitation.</p>
        </>
      );
    }

    if (tokenError) {
      return (
        <>
          <h2 className="auth-title">Invalid Invitation</h2>
          <div className="auth-message auth-message--error">{tokenError}</div>
          <p className="auth-subtitle">
            Please contact your administrator for a new invitation.
          </p>
        </>
      );
    }

    if (successMsg) {
      return (
        <>
          <h2 className="auth-title">Account Activated</h2>
          <div className="auth-message auth-message--success">{successMsg}</div>
          <Link to="/login" className="auth-back-link">
            Go to Login
          </Link>
        </>
      );
    }

    if (!tokenValid) return null;

    const greeting = inviteName ? `Hi ${inviteName}, set` : "Set";

    return (
      <>
        <h2 className="auth-title">Welcome to NIRA</h2>
        <p className="auth-subtitle">
          {greeting} a password for <strong>{inviteEmail}</strong> to activate your account.
        </p>

        {errorMsg && (
          <div className="auth-message auth-message--error">{errorMsg}</div>
        )}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-input-group">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoFocus
            />
            <button
              type="button"
              className="show-password-btn"
              onClick={() => setShowPassword(!showPassword)}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
          </div>
          <div className="auth-input-group">
            <input
              type={showConfirm ? "text" : "password"}
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
            />
            <button
              type="button"
              className="show-password-btn"
              onClick={() => setShowConfirm(!showConfirm)}
            >
              <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
            </button>
          </div>
          <p className="auth-password-requirements">
            Must be at least 8 characters with uppercase, lowercase, number, and special character.
          </p>
          <button
            type="submit"
            className="auth-submit-btn"
            disabled={loading || !password || !confirmPassword}
          >
            {loading ? "Activating..." : "Activate Account"}
          </button>
        </form>
      </>
    );
  };

  return (
    <div className="auth-page-container">
      <div className="geometric-vector-wrapper">
        <div className="geometric-vector"></div>
      </div>
      <div className="geometric-vector-wrapper">
        <div className="geometric-vector-1"></div>
      </div>
      <div className="geometric-vector-wrapper">
        <div className="geometric-vector-2"></div>
      </div>
      <div className="depth-effect-overlay"></div>

      <div className="auth-card-container">
        <div className="auth-card">
          <img src={niraLogoLong} alt="NIRA Logo" className="auth-logo" />
          <div className="auth-powered-by">Powered by: Diem360</div>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default SetPassword;
